import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Link2, Search, X, Check, CheckSquare, Square, Plus, Layers, Trash2, ArrowRightLeft, Zap, History,
  AlertTriangle, Package, Image, RefreshCw, ShoppingBag, Wallet, ChevronDown, Sparkles, Wand2,
} from 'lucide-react';
import ContextStore from '../hooks/ContextStore';
import { dispatch } from '../dispatch/dispatch';
import { actions } from '../dispatch';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';

// ─── Types ────────────────────────────────────────────────────────────────────
interface GroupRef {
  _id: string; name: string; imageUrl: string | null;
  buyingRate: number; transportFees: number; storageFees: number; packageFees: number; costPerUnit: number;
}
interface GroupDetail extends GroupRef {
  skuCount: number; sampleSkus: string[]; marjanSkuCount: number; marjanSamples: string[];
}
interface Suggestion { groupId: string; name: string; imageUrl: string | null; costPerUnit: number; score: number; confidence: 'High' | 'Medium' | 'Low' }
interface MarjanSku {
  sku: string; productId: string | null; title: string | null; gtin: string | null; unitPrice: number | null;
  orders: number; delivered: number; cancelled: number; firstSeenAt: string | null; lastOrderedAt: string | null;
  stores: string[]; units: number; returnedUnits: number; payout: number; lastSoldAt: string | null;
  image: string | null; images: string[];
  linked: boolean; source: string | null; linkedAt: string | null; linkedBy: string | null;
  group: GroupRef | null; suggestions?: Suggestion[];
  // from the Marjan catalog (PIM): products that never sold are listed too
  neverOrdered?: boolean; catalogStock?: number | null; catalogStatus?: string | null;
}
interface PendingItem {
  _id: string; sku: string; productId: string | null; title: string | null; image: string | null; orders: number; payout: number;
  groupId: string; groupName: string | null; groupImageUrl: string | null; groupCostPerUnit: number;
  score: number; confidence: 'High' | 'Medium'; matchedWords: string[];
  alternatives: { groupId: string; name: string; imageUrl: string | null; costPerUnit: number; score: number }[];
}
interface AAState { running: boolean; processed: number; total: number; matched: number; high: number; medium: number; skippedRejected: number; startedAt: string | null; finishedAt: string | null; lastError: string | null }
interface HistoryEntry {
  _id: string; actionType: string; sku?: string | null; productName?: string | null; oldGroupName?: string | null;
  newGroupName?: string | null; changedBy?: string; bulkCount?: number | null; notes?: string | null; createdAt: string;
}
interface Toast { id: number; type: 'success' | 'error' | 'info'; message: string }
interface StoreOpt { _id: string; name: string }

// ─── Helpers ──────────────────────────────────────────────────────────────────
const confidenceColor: Record<string, string> = {
  High:   'bg-emerald-500/15 text-emerald-600 border-emerald-500/20',
  Medium: 'bg-amber-500/15 text-amber-600 border-amber-500/20',
  Low:    'bg-slate-500/15 text-slate-500 border-slate-500/20',
};
const fmt = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const mad = (n: number | null | undefined) => `${Math.round(n || 0).toLocaleString('en-US')} MAD`;
const actionLabel: Record<string, string> = {
  assign: 'Assigned', bulk_assign: 'Bulk assigned', auto_assign: 'Auto-assigned', remove: 'Removed', move: 'Moved', group_created: 'Group created',
};

const ProductImage: FC<{ src?: string | null; alt?: string; size?: number }> = ({ src, alt, size = 56 }) => {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div style={{ width: size, height: size, minWidth: size }} className="flex items-center justify-center rounded-lg bg-muted border border-border">
        <Image className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }
  return <img src={src} alt={alt || 'Product'} onError={() => setErr(true)} style={{ width: size, height: size, minWidth: size }} className="rounded-lg object-cover border border-border bg-muted" />;
};

// ─── Assign modal ─────────────────────────────────────────────────────────────
const AssignModal: FC<{ items: MarjanSku[]; groups: GroupDetail[]; onConfirm: (g: GroupDetail) => void; onClose: () => void }> = ({ items, groups, onConfirm, onClose }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<GroupDetail | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const filtered = (search.trim() ? groups.filter(g => g.name.toLowerCase().includes(search.toLowerCase())) : groups).slice(0, 200);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-card border border-border shadow-dropdown overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-sm font-bold text-foreground">Assign to Product Group</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {items.length === 1 ? `Marjan SKU: ${items[0].sku}` : `${items.length} Marjan SKUs selected`} · same groups as Jumia
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors"><X className="h-4 w-4 text-muted-foreground" /></button>
        </div>
        <div className="px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2 rounded-lg border border-input bg-background px-3 py-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search groups…" className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground" />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto p-2 space-y-1">
          {filtered.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No groups found</p>}
          {filtered.map(g => (
            <button key={g._id} onClick={() => setSelected(selected?._id === g._id ? null : g)}
              className={cn('w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all duration-150 text-left',
                selected?._id === g._id ? 'border-brand bg-brand/10' : 'border-transparent hover:border-border hover:bg-accent')}>
              <ProductImage src={g.imageUrl} size={40} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{g.name}</p>
                <p className="text-xs text-muted-foreground">
                  cost {mad(g.costPerUnit)}/unit · {g.skuCount} Jumia · {g.marjanSkuCount} Marjan
                  {g.sampleSkus.length > 0 && <span className="ml-1 opacity-70">· {g.sampleSkus.slice(0, 2).join(', ')}</span>}
                </p>
              </div>
              {selected?._id === g._id && <Check className="h-4 w-4 text-brand shrink-0" />}
            </button>
          ))}
        </div>
        <div className="flex gap-2 px-5 py-4 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose} className="flex-1">Cancel</Button>
          <Button size="sm" className="flex-1 bg-brand hover:bg-brand-dark text-white" disabled={!selected} onClick={() => selected && onConfirm(selected)}>
            <Check className="h-3.5 w-3.5 mr-1.5" /> Assign
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Create group modal (creates a SHARED product group) ──────────────────────
const CreateGroupModal: FC<{ items: MarjanSku[]; token: string; onCreated: (g: GroupRef, linked: number) => void; onClose: () => void }> = ({ items, token, onCreated, onClose }) => {
  const first = items[0];
  const [form, setForm] = useState({ name: first?.title?.slice(0, 60) || first?.sku || '', buyingRate: 0, transportFees: 2, storageFees: 2, packageFees: 3, supplier: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const total = (form.buyingRate || 0) + (form.transportFees || 0) + (form.storageFees || 0) + (form.packageFees || 0);
  const create = async () => {
    if (!form.name.trim() || !form.buyingRate) { setErr('Name and buying rate are required'); return; }
    setSaving(true); setErr(null);
    const r = await dispatch(actions.marjanCreateGroup, { ...form, items: items.map(i => ({ sku: i.sku, productId: i.productId, title: i.title })) }, token);
    setSaving(false);
    if (r?.error) { setErr(r.error.message); return; }
    onCreated(r.group, r.linked);
  };
  const Num: FC<{ k: keyof typeof form; label: string }> = ({ k, label }) => (
    <label className="text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input type="number" min={0} value={form[k] as number} onChange={e => set(k, parseFloat(e.target.value) || 0)}
        className="mt-1 w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm outline-none focus:border-ring" />
    </label>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-dropdown overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-sm font-bold text-foreground">New Product Group</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Shared with Jumia · {items.length} Marjan SKU{items.length !== 1 ? 's' : ''} will be linked</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors"><X className="h-4 w-4 text-muted-foreground" /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="text-xs block">
            <span className="text-muted-foreground">Group name</span>
            <input value={form.name} onChange={e => set('name', e.target.value)} className="mt-1 w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm outline-none focus:border-ring" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Num k="buyingRate" label="Buying rate (MAD) *" />
            <Num k="transportFees" label="Transport fees" />
            <Num k="storageFees" label="Storage fees" />
            <Num k="packageFees" label="Package fees" />
          </div>
          <label className="text-xs block">
            <span className="text-muted-foreground">Supplier (optional)</span>
            <input value={form.supplier} onChange={e => set('supplier', e.target.value)} className="mt-1 w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm outline-none focus:border-ring" />
          </label>
          <div className="rounded-lg bg-muted px-3 py-2 text-xs flex items-center justify-between">
            <span className="text-muted-foreground">Total cost per unit</span><span className="font-bold text-foreground">{mad(total)}</span>
          </div>
          {err && <p className="text-xs text-destructive flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {err}</p>}
        </div>
        <div className="flex gap-2 px-5 py-4 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose} className="flex-1">Cancel</Button>
          <Button size="sm" className="flex-1 bg-brand hover:bg-brand-dark text-white" disabled={saving} onClick={create}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> {saving ? 'Creating…' : 'Create & Link'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── SKU card ─────────────────────────────────────────────────────────────────
const SkuCard: FC<{
  item: MarjanSku; selected: boolean; onToggle: () => void;
  onAssign: () => void; onCreate: () => void; onSuggestion: (s: Suggestion) => void; onMove: () => void; onRemove: () => void;
}> = ({ item, selected, onToggle, onAssign, onCreate, onSuggestion, onMove, onRemove }) => {
  const imgRef = useRef<HTMLDivElement>(null);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  const picture = item.image || item.group?.imageUrl || null;
  const handleImgEnter = () => {
    if (!picture || !imgRef.current) return;
    const r = imgRef.current.getBoundingClientRect();
    const popupW = 240;
    const left = r.right + 8 + popupW > window.innerWidth ? r.left - popupW - 8 : r.right + 8;
    const top = Math.min(r.top, window.innerHeight - 300);
    setPopupPos({ top, left });
  };
  return (
  <div className={cn('relative flex flex-col rounded-2xl border bg-card shadow-card transition-all duration-150 hover:shadow-card-md overflow-hidden',
    selected ? 'border-brand ring-2 ring-brand/20' : 'border-border')}>
    <button onClick={onToggle} className="absolute top-2.5 left-2.5 z-10 p-0.5 rounded-md bg-card/90 backdrop-blur-sm border border-border hover:border-brand transition-colors">
      {selected ? <CheckSquare className="h-4 w-4 text-brand" /> : <Square className="h-4 w-4 text-muted-foreground" />}
    </button>
    <div ref={imgRef} className="relative h-36 bg-muted flex items-center justify-center overflow-hidden" onMouseEnter={handleImgEnter} onMouseLeave={() => setPopupPos(null)}>
      {picture
        ? <img src={picture} alt="" className="w-full h-full object-cover cursor-zoom-in" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        : <ShoppingBag className="h-9 w-9 text-muted-foreground/30" />}
      {item.stores.length > 0 && (
        <span className="absolute bottom-2 right-2 rounded-md bg-black/60 text-white text-[10px] font-semibold px-1.5 py-0.5 backdrop-blur-sm">{item.stores.join(' · ')}</span>
      )}
      {item.group
        ? <span className="absolute top-2 right-2 rounded-md bg-brand/90 text-white text-[10px] font-bold px-1.5 py-0.5 backdrop-blur-sm max-w-[70%] truncate">{item.group.name}</span>
        : <span className="absolute top-2 right-2 rounded-md bg-slate-500/70 text-white text-[10px] font-semibold px-1.5 py-0.5">No group</span>}
      {popupPos && picture && (
        <div className="animate-fade-in rounded-xl overflow-hidden shadow-dropdown border border-border bg-card pointer-events-none"
          style={{ position: 'fixed', top: popupPos.top, left: popupPos.left, width: 240, zIndex: 999 }}>
          <img src={picture} alt="" style={{ width: '100%', maxHeight: 220, objectFit: 'contain', display: 'block', background: 'hsl(var(--muted))' }} />
          <div className="px-3 py-2 border-t border-border">
            {item.title && <p className="text-xs font-semibold text-foreground line-clamp-2">{item.title}</p>}
            <p className="text-[10px] text-muted-foreground font-mono">{item.sku}{item.image ? ' · Marjane picture' : ' · group picture'}</p>
          </div>
        </div>
      )}
    </div>
    <div className="flex-1 p-3 space-y-2">
      <div>
        <p className="text-xs font-bold text-foreground font-mono truncate" title={item.productId ? `Marjane id ${item.productId}` : ''}>{item.sku}</p>
        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5 leading-relaxed">{item.title || <span className="italic">No title</span>}</p>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {item.delivered > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-500/10 rounded-full px-2 py-0.5">✓ {item.delivered} delivered</span>}
        {item.orders > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-foreground/70 bg-muted rounded-full px-2 py-0.5">{item.orders} orders</span>}
        {item.payout !== 0 && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 bg-blue-500/10 rounded-full px-2 py-0.5">{mad(item.payout)} paid</span>}
        {item.unitPrice != null && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-foreground/70 bg-muted rounded-full px-2 py-0.5">sells {mad(item.unitPrice)}</span>}
        {item.neverOrdered && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-500/10 rounded-full px-2 py-0.5" title="In the Marjan catalog but never ordered — link it so it follows the warehouse stock">never ordered · Marjan stock {item.catalogStock ?? '—'}</span>}
      </div>
      {item.group && (
        <div className="rounded-lg bg-muted/60 px-2.5 py-1.5 text-[11px] flex items-center justify-between">
          <span className="text-muted-foreground">Cost / unit</span>
          <span className="font-bold text-foreground">{mad(item.group.costPerUnit)}</span>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground">
        {item.linked ? `Linked ${fmt(item.linkedAt)}${item.source === 'jumia-sku' ? ' · same SKU as Jumia' : item.source === 'suggestion' ? ' · auto' : ''}` : `First seen ${fmt(item.firstSeenAt)}`}
      </p>
      {!item.linked && item.suggestions && item.suggestions.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Zap className="h-2.5 w-2.5 text-brand" /> Suggestions</p>
          {item.suggestions.slice(0, 2).map(s => (
            <button key={s.groupId} onClick={() => onSuggestion(s)}
              className="w-full flex items-center gap-1.5 p-1.5 rounded-lg border border-border hover:border-brand hover:bg-brand/5 transition-all text-left">
              <ProductImage src={s.imageUrl} size={24} />
              <span className="flex-1 text-[11px] font-medium text-foreground truncate">{s.name}</span>
              <span className="text-[10px] text-muted-foreground shrink-0">{mad(s.costPerUnit)}</span>
              <span className={cn('text-[9px] font-bold rounded-full border px-1.5 py-0.5 shrink-0', confidenceColor[s.confidence])}>{s.confidence}</span>
            </button>
          ))}
        </div>
      )}
    </div>
    <div className="px-3 pb-3 flex gap-1.5">
      {item.linked ? (
        <>
          <Button size="sm" variant="outline" onClick={onMove} className="flex-1 h-7 text-xs"><ArrowRightLeft className="h-3 w-3 mr-1" /> Move</Button>
          <Button size="sm" variant="outline" onClick={onRemove} disabled={item.source === 'jumia-sku'} title={item.source === 'jumia-sku' ? 'Linked through the Jumia product itself' : ''}
            className="flex-1 h-7 text-xs text-destructive hover:bg-destructive/5 border-destructive/30"><Trash2 className="h-3 w-3 mr-1" /> Remove</Button>
        </>
      ) : (
        <>
          <Button size="sm" variant="outline" onClick={onAssign} className="flex-1 h-7 text-xs"><Layers className="h-3 w-3 mr-1" /> Assign</Button>
          <Button size="sm" onClick={onCreate} className="flex-1 h-7 text-xs bg-brand hover:bg-brand-dark text-white"><Plus className="h-3 w-3 mr-1" /> New Group</Button>
        </>
      )}
    </div>
  </div>
  );
};

// ─── History drawer ───────────────────────────────────────────────────────────
const HistoryDrawer: FC<{ history: HistoryEntry[]; loading: boolean; onClose: () => void }> = ({ history, loading, onClose }) => (
  <div className="fixed inset-0 z-50 flex" onClick={onClose}>
    <div className="flex-1" />
    <div className="w-full max-w-sm h-full bg-card border-l border-border shadow-dropdown overflow-y-auto flex flex-col" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between px-4 py-4 border-b border-border sticky top-0 bg-card z-10">
        <h2 className="text-sm font-bold text-foreground flex items-center gap-2"><History className="h-4 w-4 text-brand" /> Marjan Association History</h2>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors"><X className="h-4 w-4 text-muted-foreground" /></button>
      </div>
      <div className="flex-1 p-3 space-y-2">
        {loading && Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}
        {!loading && history.length === 0 && <p className="text-sm text-muted-foreground text-center py-10">No history yet</p>}
        {!loading && history.map(h => (
          <div key={h._id} className="p-3 rounded-xl border border-border bg-background space-y-1">
            <div className="flex items-center justify-between gap-2">
              <Badge variant="secondary" className="text-[10px]">{actionLabel[h.actionType] || h.actionType}</Badge>
              <span className="text-[10px] text-muted-foreground">{fmt(h.createdAt)}</span>
            </div>
            {h.sku && <p className="text-xs font-mono font-semibold text-foreground">{h.sku}</p>}
            {h.bulkCount && <p className="text-xs text-muted-foreground">{h.bulkCount} SKUs</p>}
            {h.notes && !h.sku && <p className="text-[10px] text-muted-foreground">{h.notes}</p>}
            <p className="text-[10px] text-muted-foreground">
              {h.oldGroupName && <span className="line-through mr-1">{h.oldGroupName}</span>}
              {h.newGroupName && <span className="text-brand font-medium">→ {h.newGroupName}</span>}
              {h.changedBy && <span className="ml-1 opacity-70">by {h.changedBy}</span>}
            </p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────
type Mode = 'unlinked' | 'linked' | 'by-group';
type Sort = 'payout' | 'orders' | 'newest' | 'sku';

const MarjanSkuAssociationPage: FC = () => {
  const { contextStore } = useContext(ContextStore) as any;
  const token: string = contextStore?.token;
  const navigate = useNavigate();

  const [mainTab, setMainTab] = useState<'skus' | 'auto'>('skus');
  const [mode, setMode] = useState<Mode>('unlinked');
  const [items, setItems] = useState<MarjanSku[]>([]);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<GroupDetail[]>([]);
  const [stores, setStores] = useState<StoreOpt[]>([]);
  const [storeId, setStoreId] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort>('payout');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [limit, setLimit] = useState(60);
  const [counts, setCounts] = useState<{ unlinked: number; linked: number; unlinkedPayout: number } | null>(null);

  const [filterGroupId, setFilterGroupId] = useState('');
  const [groupPickerOpen, setGroupPickerOpen] = useState(false);
  const [groupPickerSearch, setGroupPickerSearch] = useState('');
  const groupPickerRef = useRef<HTMLDivElement>(null);

  const [assignTarget, setAssignTarget] = useState<MarjanSku[] | null>(null);
  const [createTarget, setCreateTarget] = useState<MarjanSku[] | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [aaState, setAaState] = useState<AAState | null>(null);
  const [pending, setPending] = useState<PendingItem[]>([]);
  const [pendingCounts, setPendingCounts] = useState({ high: 0, medium: 0, rejected: 0 });
  const [pendingLoading, setPendingLoading] = useState(false);
  const [aaConfTab, setAaConfTab] = useState<'High' | 'Medium'>('High');
  const [decisions, setDecisions] = useState<Record<string, 'approved' | 'rejected'>>({});
  const [applying, setApplying] = useState(false);
  const aaPollRef = useRef<any>(null);
  const [imgSyncing, setImgSyncing] = useState(false);

  // listings priced under their linked product's cost (user 2026-10-14) — usually a wrong link
  const [belowCost, setBelowCost] = useState<{ sku: string; gtin: string; productId: string | null; title: string | null; image: string | null; price: number; group: string; cost: number; floorPrice: number }[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(0);
  const addToast = useCallback((type: Toast['type'], message: string) => {
    const id = ++toastId.current;
    setToasts(t => [...t, { id, type, message }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  }, []);

  // ── Loading ────────────────────────────────────────────────────────────────
  const loadGroups = useCallback(async () => {
    const g = await dispatch(actions.getMarjanAssocGroups, {}, token);
    if (Array.isArray(g)) setGroups(g);
  }, [token]);

  const loadCounts = useCallback(async () => {
    const all = await dispatch(actions.getMarjanAssocSkus, {}, token, { mode: 'all', storeId });
    if (Array.isArray(all)) {
      const un = all.filter((x: MarjanSku) => !x.linked);
      setCounts({ unlinked: un.length, linked: all.length - un.length, unlinkedPayout: un.reduce((s: number, x: MarjanSku) => s + x.payout, 0) });
    }
  }, [token, storeId]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    let r;
    if (mode === 'by-group') {
      r = filterGroupId ? await dispatch(actions.getMarjanAssocByGroup, {}, token, { groupId: filterGroupId }) : [];
    } else {
      r = await dispatch(actions.getMarjanAssocSkus, {}, token, { mode, storeId });
    }
    if (Array.isArray(r)) setItems(r);
    else if (r?.error) addToast('error', r.error.message);
    setLoading(false);
  }, [token, mode, storeId, filterGroupId, addToast]);

  useEffect(() => {
    loadGroups();
    dispatch(actions.getMarjanStores, {}, token).then((s: any) => { if (Array.isArray(s)) setStores(s.map((x: any) => ({ _id: x._id, name: x.name }))); });
  }, [token, loadGroups]);
  useEffect(() => { loadItems(); setSelected(new Set()); setLimit(60); }, [loadItems]);
  useEffect(() => { setLimit(60); }, [search, sort]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  useEffect(() => {
    if (!groupPickerOpen) return;
    const h = (e: MouseEvent) => { if (groupPickerRef.current && !groupPickerRef.current.contains(e.target as Node)) setGroupPickerOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [groupPickerOpen]);

  const loadBelowCost = useCallback(async () => {
    const r = await dispatch(actions.getMarjanBelowCost, {}, token);
    if (Array.isArray(r)) setBelowCost(r);
  }, [token]);
  useEffect(() => { loadBelowCost(); }, [loadBelowCost]);
  const refreshAll = useCallback(async () => { await Promise.all([loadItems(), loadCounts(), loadGroups(), loadBelowCost()]); }, [loadItems, loadCounts, loadGroups, loadBelowCost]);
  const confirmBelowCost = async (sku: string, floorPrice: number) => {
    if (!window.confirm(`The link of ${sku} is right?\n\nIts Marjane price will follow the rules: at least 40 MAD profit → about ${floorPrice} MAD.`)) return;
    const r = await dispatch(actions.confirmMarjanBelowCost, {}, token, { sku });
    if (r?.error) { addToast('error', r.error.message); return; }
    addToast('success', r.raised ? `${sku}: price raised to the Marjane minimum` : `${sku}: link confirmed`);
    loadBelowCost();
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const linkItems = async (list: MarjanSku[], group: GroupRef, source: 'manual' | 'suggestion' = 'manual') => {
    const r = await dispatch(actions.marjanBulkLink, { items: list.map(i => ({ sku: i.sku, productId: i.productId, title: i.title })), groupId: group._id, source }, token);
    if (r?.error) { addToast('error', r.error.message); return; }
    addToast('success', `${list.length === 1 ? list[0].sku : `${list.length} SKUs`} → ${group.name}`);
    setAssignTarget(null);
    setSelected(new Set());
    refreshAll();
  };
  const removeLink = async (item: MarjanSku) => {
    if (!window.confirm(`Remove the cost link of ${item.sku} from "${item.group?.name}"?`)) return;
    const r = await dispatch(actions.marjanUnlink, {}, token, { sku: item.sku });
    if (r?.error) { addToast('error', r.error.message); return; }
    addToast('info', `${item.sku} unlinked`);
    refreshAll();
  };
  const syncImages = async () => {
    setImgSyncing(true);
    const r = await dispatch(actions.marjanSyncImages, { storeId: storeId || null }, token);
    setImgSyncing(false);
    if (r?.error) { addToast('error', r.error.message); return; }
    addToast('success', r.fetched ? `${r.ok} product picture${r.ok !== 1 ? 's' : ''} fetched from Marjane` : 'All product pictures are up to date');
    if (r.fetched) refreshAll();
  };
  const openHistory = async () => {
    setShowHistory(true); setHistoryLoading(true);
    const h = await dispatch(actions.getMarjanAssocHistory, {}, token);
    if (Array.isArray(h)) setHistory(h);
    setHistoryLoading(false);
  };
  // ── Smart Auto-Associate (persistent queue, like the Jumia one) ───────────
  const loadPending = useCallback(async () => {
    setPendingLoading(true);
    const r = await dispatch(actions.getMarjanPending, {}, token, { confidence: '', limit: 500 });
    if (r?.items) {
      setPending(r.items);
      setPendingCounts({ high: r.highCount, medium: r.mediumCount, rejected: r.rejectedCount });
      if (r.state) setAaState(r.state);
    }
    setPendingLoading(false);
  }, [token]);

  useEffect(() => { if (mainTab === 'auto') { loadPending(); setDecisions({}); } }, [mainTab, loadPending]);

  // poll while the background job runs
  useEffect(() => {
    if (aaPollRef.current) { clearInterval(aaPollRef.current); aaPollRef.current = null; }
    if (!aaState?.running) return;
    aaPollRef.current = setInterval(async () => {
      const st = await dispatch(actions.marjanAutoAssociateStatus, {}, token);
      if (st && typeof st.running === 'boolean') {
        setAaState(st);
        if (!st.running) { loadPending(); refreshAll(); }
      }
    }, 1500);
    return () => { if (aaPollRef.current) clearInterval(aaPollRef.current); };
  }, [aaState?.running, token, loadPending, refreshAll]);

  const startAuto = async () => {
    const r = await dispatch(actions.marjanAutoAssociateStart, { storeId: storeId || null }, token);
    if (r?.error) { addToast('error', r.error.message); return; }
    setDecisions({});
    setAaState(prev => ({ ...(prev || { processed: 0, total: 0, matched: 0, high: 0, medium: 0, skippedRejected: 0, startedAt: null, finishedAt: null, lastError: null }), running: true, processed: 0, total: 0, matched: 0, high: 0, medium: 0 }));
  };
  const toggleDecision = (id: string, action: 'approved' | 'rejected') =>
    setDecisions(d => { const n = { ...d }; if (n[id] === action) delete n[id]; else n[id] = action; return n; });
  const decideAllVisible = (action: 'approved' | 'rejected') =>
    setDecisions(d => { const n = { ...d }; pending.filter(i => i.confidence === aaConfTab).forEach(i => { n[i._id] = action; }); return n; });
  const applyDecisions = async () => {
    const list = Object.entries(decisions).map(([id, action]) => ({ id, action }));
    if (!list.length) return;
    setApplying(true);
    const r = await dispatch(actions.applyMarjanPending, { decisions: list }, token);
    setApplying(false);
    if (r?.error) { addToast('error', r.error.message); return; }
    addToast('success', `${r.approved} linked · ${r.rejected} rejected`);
    setDecisions({});
    await loadPending();
    refreshAll();
  };
  const clearPendingAll = async () => {
    if (!window.confirm('Discard all pending suggestions? (Rejected ones stay remembered.)')) return;
    await dispatch(actions.clearMarjanPending, {}, token);
    setDecisions({});
    loadPending();
  };
  const retarget = async (item: PendingItem, groupId: string) => {
    const r = await dispatch(actions.retargetMarjanPending, { groupId }, token, { id: item._id });
    if (r?.error) { addToast('error', r.error.message); return; }
    setPending(p => p.map(x => x._id === item._id ? { ...x, ...r } : x));
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q ? items.filter(i => i.sku.toLowerCase().includes(q) || (i.title || '').toLowerCase().includes(q) || (i.group?.name || '').toLowerCase().includes(q)) : items;
    list = [...list].sort((a, b) => {
      if (sort === 'payout') return b.payout - a.payout || b.orders - a.orders;
      if (sort === 'orders') return b.orders - a.orders || b.payout - a.payout;
      if (sort === 'newest') return new Date(b.firstSeenAt || 0).getTime() - new Date(a.firstSeenAt || 0).getTime();
      return a.sku.localeCompare(b.sku);
    });
    return list;
  }, [items, search, sort]);
  const selectedItems = visible.filter(i => selected.has(i.sku));
  const toggle = (sku: string) => setSelected(s => { const n = new Set(s); n.has(sku) ? n.delete(sku) : n.add(sku); return n; });
  const allVisibleSelected = visible.length > 0 && visible.every(i => selected.has(i.sku));
  const pendingVisible = pending.filter(i => i.confidence === aaConfTab);
  const pendingTotal = pendingCounts.high + pendingCounts.medium;
  const aaRunning = !!aaState?.running;
  const nDecisions = Object.keys(decisions).length;

  return (
    <div className="min-h-screen bg-background">
      {/* Toasts */}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} className={cn('flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium shadow-dropdown border animate-fade-in pointer-events-auto',
            t.type === 'success' && 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
            t.type === 'error' && 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/40 dark:text-red-300',
            t.type === 'info' && 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300')}>
            {t.type === 'success' && <Check className="h-3.5 w-3.5 shrink-0" />}
            {t.type === 'error' && <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
            {t.type === 'info' && <Package className="h-3.5 w-3.5 shrink-0" />}
            {t.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="sticky top-0 z-30 bg-card/95 backdrop-blur border-b border-border">
        <div className="px-4 lg:px-6 py-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 mr-auto">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-red-600 to-red-400 flex items-center justify-center"><Link2 className="h-4 w-4 text-white" /></div>
            <div>
              <h1 className="text-sm font-bold text-foreground">Marjan SKU Association</h1>
              <p className="text-[11px] text-muted-foreground">Link Marjan SKUs to the same product groups as Jumia → buying cost, transport, storage &amp; package fees</p>
            </div>
          </div>
          {counts && (
            <div className="flex items-center gap-2 text-xs">
              <span className="rounded-full bg-amber-500/10 text-amber-700 font-semibold px-2.5 py-1">{counts.unlinked} unlinked · {mad(counts.unlinkedPayout)} uncosted</span>
              <span className="rounded-full bg-emerald-500/10 text-emerald-700 font-semibold px-2.5 py-1">{counts.linked} linked</span>
            </div>
          )}
          <div className="flex rounded-lg border border-input bg-muted p-0.5 gap-0.5">
            {([['skus', 'SKUs'], ['auto', '✦ Auto-Associate']] as const).map(([t, l]) => (
              <button key={t} onClick={() => setMainTab(t)} className={cn('rounded-md px-3 py-1 text-xs font-semibold transition-all', mainTab === t ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground')}>{l}</button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={syncImages} disabled={imgSyncing} className="gap-1.5 text-xs" title="Fetch pictures from the Marjane catalog for products that have none yet">
            <Image className={cn('h-3.5 w-3.5', imgSyncing && 'animate-pulse')} /> {imgSyncing ? 'Fetching…' : 'Pictures'}
          </Button>
          <Button size="sm" variant="outline" onClick={openHistory} className="gap-1.5 text-xs"><History className="h-3.5 w-3.5" /> History</Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/marjan#profit')} className="gap-1.5 text-xs"><Wallet className="h-3.5 w-3.5" /> Payouts &amp; Profit</Button>
          <Button size="sm" className="bg-brand hover:bg-brand-dark text-white" onClick={() => setCreateTarget([])}><Plus className="h-3.5 w-3.5 mr-1" /> New Group</Button>
        </div>

        {mainTab === 'skus' && (
          <div className="px-4 lg:px-6 pb-3 flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-input bg-muted p-0.5 gap-0.5">
              {([['unlinked', 'Unassociated'], ['linked', 'Associated'], ['by-group', 'By Group']] as const).map(([m, l]) => (
                <button key={m} onClick={() => setMode(m)} className={cn('rounded-md px-3 py-1 text-xs font-semibold transition-all', mode === m ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground')}>{l}</button>
              ))}
            </div>
            {mode === 'by-group' && (
              <div ref={groupPickerRef} className="relative">
                <button onClick={() => { setGroupPickerOpen(o => !o); setGroupPickerSearch(''); }}
                  className={cn('flex items-center gap-2 rounded-lg border bg-background pl-3 pr-2 py-1.5 text-sm outline-none cursor-pointer max-w-[260px] min-w-[200px]', groupPickerOpen ? 'border-ring ring-2 ring-ring/20' : 'border-input')}>
                  <span className="flex-1 text-left truncate text-sm">{filterGroupId ? (groups.find(g => g._id === filterGroupId)?.name ?? '— Select a group —') : '— Select a group —'}</span>
                  {filterGroupId && <X className="h-3 w-3 text-muted-foreground shrink-0" onClick={e => { e.stopPropagation(); setFilterGroupId(''); setGroupPickerOpen(false); }} />}
                  <ChevronDown className={cn('h-3 w-3 text-muted-foreground shrink-0 transition-transform', groupPickerOpen && 'rotate-180')} />
                </button>
                {groupPickerOpen && (
                  <div className="absolute top-[calc(100%+6px)] left-0 z-[200] bg-card border border-border rounded-xl shadow-dropdown overflow-hidden min-w-[300px]">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/40">
                      <Search className="h-3 w-3 text-muted-foreground shrink-0" />
                      <input autoFocus value={groupPickerSearch} onChange={e => setGroupPickerSearch(e.target.value)} placeholder="Search groups…" className="flex-1 text-xs bg-transparent outline-none" />
                    </div>
                    <div className="max-h-72 overflow-y-auto p-1">
                      {groups.filter(g => !groupPickerSearch.trim() || g.name.toLowerCase().includes(groupPickerSearch.toLowerCase())).slice(0, 80).map(g => (
                        <button key={g._id} onClick={() => { setFilterGroupId(g._id); setGroupPickerOpen(false); }}
                          className={cn('w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-accent', filterGroupId === g._id && 'bg-brand/10')}>
                          <ProductImage src={g.imageUrl} size={26} />
                          <span className="flex-1 text-xs truncate">{g.name}</span>
                          <span className="text-[10px] text-muted-foreground">{g.marjanSkuCount} Marjan</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 rounded-lg border border-input bg-background px-3 py-1.5 min-w-[220px]">
              <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search SKU / title / group…" className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground" />
              {search && <X className="h-3 w-3 text-muted-foreground cursor-pointer" onClick={() => setSearch('')} />}
            </div>
            <select value={storeId} onChange={e => setStoreId(e.target.value)} className="rounded-lg border border-input bg-background px-2 py-1.5 text-xs">
              <option value="">All Marjan stores</option>
              {stores.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
            <select value={sort} onChange={e => setSort(e.target.value as Sort)} className="rounded-lg border border-input bg-background px-2 py-1.5 text-xs">
              <option value="payout">Sort: most payout</option>
              <option value="orders">Sort: most orders</option>
              <option value="newest">Sort: newest</option>
              <option value="sku">Sort: SKU</option>
            </select>
            <button onClick={() => setSelected(allVisibleSelected ? new Set() : new Set(visible.map(i => i.sku)))} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
              {allVisibleSelected ? <CheckSquare className="h-3.5 w-3.5 text-brand" /> : <Square className="h-3.5 w-3.5" />} Select all ({visible.length})
            </button>
            <button onClick={refreshAll} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} /> Refresh</button>
            {selectedItems.length > 0 && (
              <div className="ml-auto flex items-center gap-2 rounded-lg bg-brand/10 border border-brand/30 px-3 py-1.5">
                <span className="text-xs font-semibold text-brand">{selectedItems.length} selected</span>
                <Button size="sm" className="h-7 text-xs bg-brand hover:bg-brand-dark text-white" onClick={() => setAssignTarget(selectedItems)}><Layers className="h-3 w-3 mr-1" /> Assign to group</Button>
                {mode === 'unlinked' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setCreateTarget(selectedItems)}><Plus className="h-3 w-3 mr-1" /> New group</Button>}
                <button onClick={() => setSelected(new Set())} className="p-1 rounded hover:bg-accent"><X className="h-3.5 w-3.5 text-muted-foreground" /></button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4 lg:p-6">
        {mainTab === 'skus' && (
          <>
            {belowCost.length > 0 && (
              <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 dark:bg-red-900/20 p-3">
                <div className="flex items-center gap-2 text-sm font-bold text-red-800 dark:text-red-300 mb-1"><AlertTriangle className="h-4 w-4" /> {belowCost.length} Marjane listing{belowCost.length > 1 ? 's' : ''} priced UNDER the cost of the product linked to {belowCost.length > 1 ? 'them' : 'it'}</div>
                <p className="text-xs text-red-700 dark:text-red-300 mb-2">Usually the wrong product is linked. Check each one: if the link is wrong, change it; if it is right, confirm and its price follows the Marjane rules (at least 40 MAD profit). Prices are not touched until you decide.</p>
                <div className="flex flex-col gap-2">
                  {belowCost.map(b => (
                    <div key={b.sku + b.gtin} className="flex flex-wrap items-center gap-3 rounded-xl bg-card border border-border p-2">
                      {b.image ? <img src={b.image} alt="" className="h-14 w-14 rounded-lg object-contain bg-white" /> : <div className="h-14 w-14 rounded-lg bg-muted" />}
                      <div className="min-w-[200px] flex-1">
                        <div className="text-xs font-semibold text-foreground">{b.title || b.sku}</div>
                        <div className="text-[11px] text-muted-foreground">SKU {b.sku} · sold at <b>{b.price} MAD</b>{b.productId && <> · <a className="underline" href={`https://www.marjanemall.ma/p/${b.productId.toLowerCase()}`} target="_blank" rel="noreferrer">see on Marjane</a></>}</div>
                        <div className="text-[11px] text-red-700 dark:text-red-300">Linked to <b>{b.group}</b> — cost {b.cost} MAD</div>
                      </div>
                      <button className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted" onClick={() => setAssignTarget([{ sku: b.sku, productId: b.productId, title: b.title, gtin: b.gtin } as MarjanSku])}>Change link</button>
                      <button className="rounded-lg bg-emerald-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-emerald-700" onClick={() => confirmBelowCost(b.sku, b.floorPrice)}>✓ Link is right — apply the rules</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {loading && <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">{Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}</div>}
            {!loading && mode === 'by-group' && !filterGroupId && <p className="py-16 text-center text-sm text-muted-foreground">Pick a product group to see its Marjan SKUs.</p>}
            {!loading && !(mode === 'by-group' && !filterGroupId) && visible.length === 0 && (
              <div className="py-16 text-center">
                <Sparkles className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-foreground">{mode === 'unlinked' ? 'Every Marjan SKU is linked to a product group' : 'Nothing here'}</p>
                <p className="text-xs text-muted-foreground mt-1">{mode === 'unlinked' ? 'Costs now apply to all Marjan profit calculations.' : 'Try another filter or search.'}</p>
              </div>
            )}
            {!loading && visible.length > 0 && (
              <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
                {visible.slice(0, limit).map(i => (
                  <SkuCard key={i.sku} item={i} selected={selected.has(i.sku)} onToggle={() => toggle(i.sku)}
                    onAssign={() => setAssignTarget([i])} onCreate={() => setCreateTarget([i])}
                    onSuggestion={s => linkItems([i], { _id: s.groupId, name: s.name, imageUrl: s.imageUrl, buyingRate: 0, transportFees: 0, storageFees: 0, packageFees: 0, costPerUnit: s.costPerUnit }, 'suggestion')}
                    onMove={() => setAssignTarget([i])} onRemove={() => removeLink(i)} />
                ))}
              </div>
            )}
            {!loading && visible.length > limit && (
              <div className="flex items-center justify-center gap-3 py-5">
                <span className="text-xs text-muted-foreground">Showing {limit} of {visible.length}</span>
                <Button size="sm" variant="outline" onClick={() => setLimit(l => l + 60)}>Show 60 more</Button>
                <Button size="sm" variant="outline" onClick={() => setLimit(visible.length)}>Show all</Button>
              </div>
            )}
          </>
        )}

        {mainTab === 'auto' && (
          <div className="space-y-5">
            <div className="rounded-2xl border border-border bg-card p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand/10"><Wand2 className="h-6 w-6 text-brand" /></div>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-bold text-foreground">Smart Auto-Associate</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Analyses Marjan product titles and matches unassociated SKUs to your existing product groups (same groups as Jumia).
                  Nothing is saved until you review and accept each suggestion. Rejected pairs are remembered and never proposed again.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {pendingTotal > 0 && !aaRunning && (
                  <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={clearPendingAll}><X className="h-3.5 w-3.5 mr-1" /> Clear all</Button>
                )}
                <Button size="sm" className="bg-brand hover:bg-brand-dark text-white gap-1.5" onClick={startAuto} disabled={aaRunning}>
                  <Wand2 className={cn('h-3.5 w-3.5', aaRunning && 'animate-pulse')} />
                  {aaRunning ? 'Analysing…' : pendingTotal > 0 ? 'Re-run' : 'Run Auto-Associate'}
                </Button>
              </div>
            </div>

            {aaRunning && aaState && (
              <div className="rounded-xl border border-brand/20 bg-brand/5 p-4 space-y-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-brand">Analysing Marjan SKUs…</span>
                  <span className="text-muted-foreground">{aaState.processed.toLocaleString()} / {aaState.total.toLocaleString()}</span>
                </div>
                <div className="w-full h-2 rounded-full bg-brand/15 overflow-hidden">
                  <div className="h-full rounded-full bg-brand transition-all duration-500" style={{ width: aaState.total > 0 ? `${Math.round((aaState.processed / aaState.total) * 100)}%` : '5%' }} />
                </div>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  <span>Matched: <strong className="text-foreground">{aaState.matched}</strong></span>
                  <span>High: <strong className="text-emerald-600">{aaState.high}</strong></span>
                  <span>Medium: <strong className="text-amber-600">{aaState.medium}</strong></span>
                </div>
              </div>
            )}

            {aaState?.lastError && !aaRunning && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-700 flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5" /> Last run failed: {aaState.lastError}</div>
            )}

            {!aaRunning && pendingTotal > 0 && (
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-2.5">
                  <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                  <div><p className="text-xs font-bold text-emerald-700">{pendingCounts.high} High Confidence</p><p className="text-[10px] text-emerald-600/70">Score ≥ 75% — very likely correct</p></div>
                </div>
                <div className="flex items-center gap-2 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-2.5">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                  <div><p className="text-xs font-bold text-amber-700">{pendingCounts.medium} Medium Confidence</p><p className="text-[10px] text-amber-600/70">Score 50–74% — review carefully</p></div>
                </div>
                {pendingCounts.rejected > 0 && (
                  <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-4 py-2.5">
                    <X className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div><p className="text-xs font-bold text-foreground/70">{pendingCounts.rejected} remembered rejections</p><p className="text-[10px] text-muted-foreground">Never proposed again</p></div>
                  </div>
                )}
                <div className="flex-1" />
                {nDecisions > 0 && (
                  <Button size="sm" className="bg-brand hover:bg-brand-dark text-white gap-1.5" onClick={applyDecisions} disabled={applying}>
                    {applying ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Applying…</> : <><Check className="h-3.5 w-3.5" /> Apply {nDecisions} decision{nDecisions !== 1 ? 's' : ''}</>}
                  </Button>
                )}
              </div>
            )}

            {!aaRunning && aaState?.finishedAt && pendingTotal === 0 && !pendingLoading && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted"><Package className="h-7 w-7 text-muted-foreground/40" /></div>
                <p className="text-sm font-semibold text-foreground">No pending suggestions</p>
                <p className="text-xs text-muted-foreground text-center max-w-xs">Every match has been reviewed, or no unassociated Marjan title was similar enough to an existing group. Create groups first, then re-run.</p>
              </div>
            )}
            {!aaRunning && !aaState?.finishedAt && pendingTotal === 0 && !pendingLoading && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10"><Wand2 className="h-7 w-7 text-brand/60" /></div>
                <p className="text-sm font-semibold text-foreground">Ready to analyse</p>
                <p className="text-xs text-muted-foreground text-center max-w-xs">Click "Run Auto-Associate" to scan all unassociated Marjan SKUs and find matching groups. Runs in the background, usually a few seconds.</p>
              </div>
            )}

            {pendingTotal > 0 && !aaRunning && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex rounded-lg border border-input bg-muted p-0.5 gap-0.5">
                    {(['High', 'Medium'] as const).map(conf => (
                      <button key={conf} onClick={() => setAaConfTab(conf)}
                        className={cn('rounded-md px-3 py-1 text-xs font-semibold transition-all flex items-center gap-1.5', aaConfTab === conf ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground')}>
                        <span className={cn('h-1.5 w-1.5 rounded-full', conf === 'High' ? 'bg-emerald-500' : 'bg-amber-500')} />
                        {conf}<span className="ml-0.5 text-[10px] text-muted-foreground">({conf === 'High' ? pendingCounts.high : pendingCounts.medium})</span>
                      </button>
                    ))}
                  </div>
                  <div className="flex-1" />
                  <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => decideAllVisible('approved')}><CheckSquare className="h-3.5 w-3.5 text-emerald-600" /> Accept all {aaConfTab}</Button>
                  <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => decideAllVisible('rejected')}><X className="h-3.5 w-3.5 text-red-500" /> Reject all {aaConfTab}</Button>
                </div>

                {pendingLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-36 rounded-xl" />)}</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {pendingVisible.map(item => {
                      const decision = decisions[item._id];
                      return (
                        <div key={item._id} className={cn('rounded-xl border bg-card p-3 space-y-3 transition-all',
                          decision === 'approved' && 'border-emerald-500/40 bg-emerald-500/5',
                          decision === 'rejected' && 'border-red-400/30 bg-red-500/5 opacity-60',
                          !decision && 'border-border hover:border-brand/30')}>
                          <div className="flex items-center gap-2">
                            <div className="h-16 w-16 shrink-0 rounded-lg border border-border bg-muted overflow-hidden" title="Marjane picture">
                              {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="h-6 w-6 text-muted-foreground/30" /></div>}
                            </div>
                            <div className="flex flex-col items-center gap-0.5 shrink-0">
                              <ArrowRightLeft className="h-3.5 w-3.5 text-muted-foreground/50" />
                              <span className={cn('text-[9px] font-bold px-1.5 py-0.5 rounded-full', item.confidence === 'High' ? 'bg-emerald-500/15 text-emerald-700' : 'bg-amber-500/15 text-amber-700')}>{Math.round(item.score * 100)}%</span>
                            </div>
                            <div className="h-16 w-16 shrink-0 rounded-lg border border-border bg-muted overflow-hidden" title="Group picture">
                              {item.groupImageUrl ? <img src={item.groupImageUrl} alt="" className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : <div className="w-full h-full flex items-center justify-center"><Layers className="h-6 w-6 text-muted-foreground/30" /></div>}
                            </div>
                            <div className="flex-1 min-w-0 ml-1">
                              <p className="text-[10px] text-muted-foreground truncate font-mono">{item.sku} · {item.orders} orders · {mad(item.payout)}</p>
                              <p className="text-xs font-semibold text-foreground line-clamp-2 leading-tight">{item.title || item.sku}</p>
                              <p className="text-[10px] text-brand font-medium truncate mt-0.5">→ {item.groupName} <span className="text-muted-foreground">· {mad(item.groupCostPerUnit)}/unit</span></p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1 items-center">
                            {item.matchedWords.slice(0, 5).map((w, i) => <span key={i} className="text-[9px] bg-brand/10 text-brand/80 font-semibold px-1.5 py-0.5 rounded-full">{w}</span>)}
                            {item.alternatives.length > 0 && (
                              <select value="" onChange={e => { if (e.target.value) retarget(item, e.target.value); }} className="ml-auto rounded-md border border-input bg-background px-1.5 py-0.5 text-[10px] max-w-[160px]">
                                <option value="">other match…</option>
                                {item.alternatives.map(a => <option key={a.groupId} value={a.groupId}>{a.name} ({Math.round(a.score * 100)}%)</option>)}
                              </select>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => toggleDecision(item._id, 'approved')}
                              className={cn('flex-1 flex items-center justify-center gap-1 rounded-lg border py-1.5 text-xs font-semibold transition-all', decision === 'approved' ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-input bg-background text-muted-foreground hover:border-emerald-500 hover:text-emerald-600')}>
                              <Check className="h-3 w-3" /> Accept
                            </button>
                            <button onClick={() => toggleDecision(item._id, 'rejected')}
                              className={cn('flex-1 flex items-center justify-center gap-1 rounded-lg border py-1.5 text-xs font-semibold transition-all', decision === 'rejected' ? 'bg-red-500 border-red-500 text-white' : 'border-input bg-background text-muted-foreground hover:border-red-400 hover:text-red-500')}>
                              <X className="h-3 w-3" /> Reject
                            </button>
                            <button onClick={() => setAssignTarget([{ ...item, gtin: null, unitPrice: null, delivered: 0, cancelled: 0, firstSeenAt: null, lastOrderedAt: null, stores: [], units: 0, returnedUnits: 0, lastSoldAt: null, images: [], linked: false, source: null, linkedAt: null, linkedBy: null, group: null } as MarjanSku])}
                              className="flex items-center justify-center gap-1 rounded-lg border border-input bg-background px-2 py-1.5 text-xs text-muted-foreground hover:border-brand hover:text-brand" title="Pick another group manually">
                              <Layers className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {nDecisions > 0 && (
                  <div className="sticky bottom-4 flex justify-center pointer-events-none">
                    <div className="pointer-events-auto flex items-center gap-3 rounded-2xl border border-brand/30 bg-card/95 backdrop-blur shadow-lg px-5 py-3">
                      <span className="text-xs text-muted-foreground">
                        <strong className="text-foreground">{Object.values(decisions).filter(d => d === 'approved').length}</strong> accepted ·{' '}
                        <strong className="text-foreground">{Object.values(decisions).filter(d => d === 'rejected').length}</strong> rejected
                      </span>
                      <Button size="sm" className="bg-brand hover:bg-brand-dark text-white gap-1.5" onClick={applyDecisions} disabled={applying}>
                        {applying ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Applying…</> : <><Check className="h-3.5 w-3.5" /> Apply {nDecisions} decisions</>}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {assignTarget && <AssignModal items={assignTarget} groups={groups} onClose={() => setAssignTarget(null)} onConfirm={g => linkItems(assignTarget, g)} />}
      {createTarget && (
        <CreateGroupModal items={createTarget} token={token} onClose={() => setCreateTarget(null)}
          onCreated={(g, linked) => { setCreateTarget(null); addToast('success', `Group "${g.name}" created${linked ? ` · ${linked} SKU${linked !== 1 ? 's' : ''} linked` : ''}`); setSelected(new Set()); refreshAll(); }} />
      )}
      {showHistory && <HistoryDrawer history={history} loading={historyLoading} onClose={() => setShowHistory(false)} />}
    </div>
  );
};

export default MarjanSkuAssociationPage;
